import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { MAX_PROJECT_SIZE_BYTES, formatBytes } from "../services/projectService";
import {
  MoreVertical,
  FilePlus,
  FileText,
  FileCode,
  Trash2,
  Edit2,
  Download,
  Upload,
  X,
  Package,
  ChevronDown,
  CheckCircle2,
  Loader2,
  Search,
} from "lucide-react";
import { FaPython, FaJsSquare } from "react-icons/fa";

const POPULAR_PYTHON_PACKAGES = [
  { name: "numpy", desc: "Fast multi-dimensional arrays & numerical math" },
  { name: "sympy", desc: "Symbolic mathematics, calculus & algebra" },
  { name: "matplotlib", desc: "2D plots, charts, and visualizations" },
  { name: "pandas", desc: "Data structures and data analysis tables" },
  { name: "scipy", desc: "Scientific computing, optimization & signal processing" },
  { name: "pillow", desc: "Image loading, editing, and transformations" },
  { name: "requests", desc: "HTTP requests and REST API client" },
];

/**
 * Reusable PictoBlox-inspired ProjectFileManager component.
 * - Upper Section: "Project Files" (Language-specific file creation, rename, delete, download, floating '+' button)
 * - Lower Section: "Modules/Libraries" (Real installed packages, Pyodide auto-install status, floating PIP package button)
 * - Resizer handle on the right edge
 * - Light and Dark theme support
 */
const ProjectFileManager = ({
  files = [],
  activeFileId = "main",
  onSelectFile = () => {},
  onCreateFile = () => {},
  onDeleteFile = () => {},
  onRenameFile = () => {},
  onDownloadFile = null,
  installedPackages = [],
  onInstallPackage = null,
  isInstallingPackage = false,
  width = 200,
  onResizeMouseDown = null,
  theme = "dark", // "dark" | "light"
  defaultLanguage = "python", // "python" | "javascript"
  canModifyFiles = true,
  className = "",
}) => {
  const isDark = theme === "dark";

  // Context Menu state
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // New File state
  const [isCreating, setIsCreating] = useState(false);
  const [targetExt, setTargetExt] = useState(defaultLanguage === "javascript" ? ".js" : ".py");
  const [newItemName, setNewItemName] = useState("");

  // Rename state
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingFileName, setEditingFileName] = useState("");

  // PIP Package Manager Modal state (Image 3 & 4)
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [manualPkgInput, setManualPkgInput] = useState("");
  const [installFeedback, setInstallFeedback] = useState(null);

  // Modules/Libraries panel collapse state (default collapsed if no packages installed)
  const [isModulesCollapsed, setIsModulesCollapsed] = useState(() => {
    return !installedPackages || installedPackages.length === 0;
  });

  // Height state for Modules/Libraries panel (Min: 90px, Max: 380px)
  const [modulesHeight, setModulesHeight] = useState(180);
  const isResizingModulesRef = useRef(false);

  // Auto-expand when first package is installed
  const prevPkgCountRef = useRef(installedPackages ? installedPackages.length : 0);
  useEffect(() => {
    const prevCount = prevPkgCountRef.current;
    const currentCount = installedPackages ? installedPackages.length : 0;
    if (prevCount === 0 && currentCount > 0) {
      setIsModulesCollapsed(false);
    }
    prevPkgCountRef.current = currentCount;
  }, [installedPackages]);

  const handleModulesResizeMouseDown = (e) => {
    e.preventDefault();
    isResizingModulesRef.current = true;
    document.body.style.cursor = "row-resize";
    document.body.style.userSelect = "none";

    const startY = e.clientY;
    const startHeight = modulesHeight;

    const handleMouseMove = (moveEvent) => {
      if (!isResizingModulesRef.current) return;
      const deltaY = startY - moveEvent.clientY;
      const newHeight = Math.min(Math.max(startHeight + deltaY, 90), 380);
      setModulesHeight(newHeight);
    };

    const handleMouseUp = () => {
      isResizingModulesRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
  };

  // File upload input ref
  const uploadInputRef = useRef(null);

  // Close context menu on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  // Start creating a file
  const startCreate = (ext = defaultLanguage === "javascript" ? ".js" : ".py") => {
    setIsMenuOpen(false);
    setTargetExt(ext);
    setNewItemName("");
    setIsCreating(true);
  };

  const handleFinishCreate = () => {
    let name = newItemName.trim();
    if (!name) {
      setIsCreating(false);
      return;
    }

    if (!name.includes(".")) {
      name += targetExt;
    }

    if (files.some((f) => f.name.toLowerCase() === name.toLowerCase())) {
      alert(`A file named "${name}" already exists in the project.`);
      return;
    }

    let initialContent = "";
    if (name.endsWith(".py")) {
      initialContent = `# ${name}\n`;
    } else if (name.endsWith(".js")) {
      initialContent = `// ${name}\n`;
    } else if (name.endsWith(".txt")) {
      initialContent = `# ${name}\n`;
    }

    onCreateFile({
      name,
      content: initialContent,
      type: "file",
      parentId: null,
      path: name,
    });

    setIsCreating(false);
    setNewItemName("");
  };

  // Start inline rename
  const handleStartRename = (file, e) => {
    if (e) e.stopPropagation();
    if (file.isEntry || file.isReadOnly) return;
    setEditingFileId(file.id);
    setEditingFileName(file.name);
  };

  const handleFinishRename = () => {
    let name = editingFileName.trim();
    if (!name || !editingFileId) {
      setEditingFileId(null);
      return;
    }

    const currentFile = files.find((f) => f.id === editingFileId);
    if (!currentFile) {
      setEditingFileId(null);
      return;
    }

    if (!name.includes(".")) {
      const ext = currentFile.name.includes(".")
        ? currentFile.name.slice(currentFile.name.lastIndexOf("."))
        : defaultLanguage === "javascript" ? ".js" : ".py";
      name += ext;
    }

    if (
      files.some(
        (f) => f.id !== editingFileId && f.name.toLowerCase() === name.toLowerCase()
      )
    ) {
      alert(`A file named "${name}" already exists in the project.`);
      return;
    }

    onRenameFile(editingFileId, name);
    setEditingFileId(null);
  };

  // File download helper
  const handleDownload = (file, e) => {
    if (e) e.stopPropagation();
    if (onDownloadFile) {
      onDownloadFile(file);
      return;
    }
    const blob = new Blob([file.content || ""], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Upload local file into project
  const handleUploadFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_PROJECT_SIZE_BYTES) {
      toast.error(
        `File "${file.name}" (${formatBytes(file.size)}) exceeds the maximum allowed size limit of ${formatBytes(MAX_PROJECT_SIZE_BYTES)}.`
      );
      if (uploadInputRef.current) uploadInputRef.current.value = "";
      setIsMenuOpen(false);
      return;
    }

    const reader = new FileReader();
    reader.onload = (loadEvent) => {
      const content = loadEvent.target?.result || "";
      let fileName = file.name;
      if (files.some((f) => f.name.toLowerCase() === fileName.toLowerCase())) {
        const parts = fileName.split(".");
        const ext = parts.length > 1 ? `.${parts.pop()}` : "";
        fileName = `${parts.join(".")}_copy${ext}`;
      }
      onCreateFile({
        name: fileName,
        content: String(content),
        type: "file",
        parentId: null,
        path: fileName,
      });
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    };
    reader.readAsText(file);
    setIsMenuOpen(false);
  };

  const handleManualInstall = (pkg) => {
    const target = (pkg || manualPkgInput).trim().toLowerCase();
    if (!target) return;
    if (installedPackages.includes(target)) {
      setInstallFeedback(`'${target}' is already installed.`);
      setTimeout(() => setInstallFeedback(null), 2500);
      return;
    }
    if (onInstallPackage) {
      onInstallPackage(target);
      setInstallFeedback(`Installing ${target}...`);
      setTimeout(() => setInstallFeedback(null), 3000);
    }
    setManualPkgInput("");
  };

  const getFileIcon = (file) => {
    if (file.name.endsWith(".js") || file.name.endsWith(".jsx")) {
      return <FaJsSquare className="w-3.5 h-3.5 shrink-0 text-[#F7DF1E]" />;
    }
    if (file.name.endsWith(".py")) {
      return <FaPython className="w-3.5 h-3.5 shrink-0 text-[#38bdf8]" />;
    }
    if (file.name.endsWith(".txt") || file.name.endsWith(".md")) {
      return <FileText className={`w-3.5 h-3.5 shrink-0 ${isDark ? "text-slate-400" : "text-slate-500"}`} />;
    }
    return <FileCode className={`w-3.5 h-3.5 shrink-0 ${isDark ? "text-purple-400" : "text-purple-500"}`} />;
  };

  return (
    <div
      className={`relative flex select-none shrink-0 ${className} ${
        isDark ? "bg-[#181818] text-slate-200" : "bg-white text-slate-800"
      }`}
      style={{ width: `${width}px` }}
    >
      {/* Hidden file input for uploading */}
      <input
        ref={uploadInputRef}
        type="file"
        onChange={handleUploadFile}
        className="hidden"
        accept=".py,.js,.txt,.json,.csv,.md"
      />

      {/* Main Container: Split into Upper Project Files & Lower Modules/Libraries */}
      <div className="flex-1 flex flex-col h-full overflow-hidden border-r border-slate-700/50">
        
        {/* ========================================================================= */}
        {/* UPPER SECTION: PROJECT FILES                                              */}
        {/* ========================================================================= */}
        <div className="flex-1 flex flex-col overflow-hidden relative min-h-[120px]">
          
          {/* Section Header */}
          <div
            className={`h-9 px-3 flex items-center justify-between border-b shrink-0 ${
              isDark
                ? "bg-[#1f242c] border-[#30363d] text-slate-300"
                : "bg-slate-100/90 border-slate-200 text-slate-700"
            }`}
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-xs font-bold truncate">Project Files</span>
            </div>

            {/* Header Action Buttons: 3-dots Context Menu */}
            <div className="flex items-center gap-1">
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setIsMenuOpen((prev) => !prev)}
                  title="Project options"
                  className={`p-1 rounded hover:bg-slate-700/40 transition cursor-pointer ${
                    isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>

                {/* 3-dots Context Menu (Strictly filtered by project language) */}
                {isMenuOpen && (
                  <div
                    className={`absolute right-0 top-full mt-1 w-52 rounded-xl py-1.5 z-50 text-xs code-workspace-dark-menu ${
                      isDark
                        ? "bg-[#1f242c] text-slate-200"
                        : "bg-white text-slate-800"
                    }`}
                    style={{
                      border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
                      outline: "none",
                      boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.8), 0 8px 10px -6px rgba(0, 0, 0, 0.8)",
                    }}
                  >
                    {canModifyFiles && (
                      <>
                        {defaultLanguage === "javascript" ? (
                          <button
                            type="button"
                            onClick={() => startCreate(".js")}
                            className={`w-full px-3 py-1.5 text-left flex items-center gap-2.5 transition cursor-pointer ${
                              isDark ? "hover:bg-purple-600/30 text-slate-200" : "hover:bg-purple-50 text-slate-800"
                            }`}
                            style={{ border: "none", outline: "none" }}
                          >
                            <FaJsSquare className="w-3.5 h-3.5 text-[#F7DF1E]" />
                            <span>Create empty JavaScript file</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startCreate(".py")}
                            className={`w-full px-3 py-1.5 text-left flex items-center gap-2.5 transition cursor-pointer ${
                              isDark ? "hover:bg-purple-600/30 text-slate-200" : "hover:bg-purple-50 text-slate-800"
                            }`}
                            style={{ border: "none", outline: "none" }}
                          >
                            <FaPython className="w-3.5 h-3.5 text-[#38bdf8]" />
                            <span>Create empty Python file</span>
                          </button>
                        )}

                        <button
                          type="button"
                          onClick={() => startCreate(".txt")}
                          className={`w-full px-3 py-1.5 text-left flex items-center gap-2.5 transition cursor-pointer ${
                            isDark ? "hover:bg-purple-600/30 text-slate-200" : "hover:bg-purple-50 text-slate-800"
                          }`}
                          style={{ border: "none", outline: "none" }}
                        >
                          <FileText className="w-3.5 h-3.5 text-slate-400" />
                          <span>Create empty text file</span>
                        </button>

                        <div
                          className="my-1 divider-line"
                          style={{
                            height: "1px",
                            backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#e2e8f0",
                            border: "none",
                          }}
                        />

                        <button
                          type="button"
                          onClick={() => uploadInputRef.current?.click()}
                          className={`w-full px-3 py-1.5 text-left flex items-center gap-2.5 transition cursor-pointer ${
                            isDark ? "hover:bg-purple-600/30 text-slate-200" : "hover:bg-purple-50 text-slate-800"
                          }`}
                          style={{ border: "none", outline: "none" }}
                        >
                          <Upload className="w-3.5 h-3.5 text-emerald-400" />
                          <span>Upload / Import file</span>
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* New File Inline Input */}
          {isCreating && (
            <div
              className={`p-2 border-b ${
                isDark ? "bg-[#21262d] border-[#30363d]" : "bg-purple-50 border-purple-200"
              }`}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <FilePlus className="w-3.5 h-3.5 text-purple-400" />
                <span className="text-[11px] font-bold">New File ({targetExt})</span>
              </div>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleFinishCreate();
                  if (e.key === "Escape") setIsCreating(false);
                }}
                placeholder="eg. : module"
                autoFocus
                className={`w-full text-xs rounded px-2 py-1 outline-none border focus:ring-1 focus:ring-purple-500 ${
                  isDark
                    ? "bg-[#0d1117] border-purple-500 text-white"
                    : "bg-white border-purple-400 text-slate-900"
                }`}
              />
              <div className="flex justify-end gap-1.5 mt-2">
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className={`px-2 py-0.5 text-[10px] rounded cursor-pointer ${
                    isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleFinishCreate}
                  className="px-2.5 py-0.5 text-[10px] bg-purple-600 hover:bg-purple-500 text-white rounded font-bold cursor-pointer transition shadow-xs"
                >
                  Add
                </button>
              </div>
            </div>
          )}

          {/* File List */}
          <div className="flex-1 overflow-y-auto py-1 space-y-0.5 relative">
            {files.map((file) => {
              const isActive = file.id === activeFileId;
              return (
                <div
                  key={file.id}
                  onClick={() => onSelectFile(file.id)}
                  className={`group px-2.5 py-1.5 flex items-center justify-between text-xs cursor-pointer transition-colors ${
                    isActive
                      ? isDark
                        ? "bg-[#2d333b] text-purple-300 font-bold border-l-2 border-purple-500"
                        : "bg-purple-50 text-purple-700 font-bold border-l-2 border-purple-600"
                      : isDark
                      ? "text-slate-300 hover:bg-[#22272e] hover:text-white"
                      : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  <div className="flex items-center gap-2 truncate flex-1 min-w-0">
                    {getFileIcon(file)}
                    {editingFileId === file.id ? (
                      <input
                        type="text"
                        value={editingFileName}
                        onChange={(e) => setEditingFileName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleFinishRename();
                          if (e.key === "Escape") setEditingFileId(null);
                        }}
                        onBlur={handleFinishRename}
                        autoFocus
                        className={`w-full rounded px-1 text-xs outline-none border focus:ring-1 focus:ring-purple-500 ${
                          isDark
                            ? "bg-[#0d1117] border-purple-500 text-white"
                            : "bg-white border-purple-400 text-slate-900"
                        }`}
                      />
                    ) : (
                      <span className="truncate text-[11px] select-none">{file.name}</span>
                    )}
                  </div>

                  {/* Actions for Entry file (Download only) */}
                  {file.isEntry && editingFileId !== file.id && (
                    <div className="hidden group-hover:flex items-center gap-1 shrink-0 ml-1">
                      <button
                        type="button"
                        onClick={(e) => handleDownload(file, e)}
                        title="Download file"
                        className={`p-0.5 transition cursor-pointer ${
                          isDark ? "text-slate-400 hover:text-purple-300" : "text-slate-500 hover:text-purple-600"
                        }`}
                      >
                        <Download className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  {/* Actions (Rename, Download, Delete) */}
                  {!file.isEntry && !file.isReadOnly && editingFileId !== file.id && (
                    <div className="hidden group-hover:flex items-center gap-1 shrink-0 ml-1">
                      <button
                        type="button"
                        onClick={(e) => handleStartRename(file, e)}
                        title="Rename file"
                        className={`p-0.5 transition cursor-pointer ${
                          isDark ? "text-slate-400 hover:text-purple-300" : "text-slate-500 hover:text-purple-600"
                        }`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => handleDownload(file, e)}
                        title="Download file"
                        className={`p-0.5 transition cursor-pointer ${
                          isDark ? "text-slate-400 hover:text-purple-300" : "text-slate-500 hover:text-purple-600"
                        }`}
                      >
                        <Download className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm(`Delete file "${file.name}"?`)) {
                            onDeleteFile(file.id);
                          }
                        }}
                        title="Delete file"
                        className={`p-0.5 transition cursor-pointer ${
                          isDark ? "text-slate-400 hover:text-red-400" : "text-slate-500 hover:text-red-500"
                        }`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  {file.isEntry && (
                    <span
                      className={`text-[9px] uppercase tracking-wider font-extrabold px-1 rounded ${
                        isDark ? "bg-slate-800 text-slate-400" : "bg-slate-200 text-slate-600"
                      }`}
                    >
                      main
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ========================================================================= */}
        {/* HORIZONTAL RESIZER DIVIDER BETWEEN PROJECT FILES & MODULES               */}
        {/* ========================================================================= */}
        {defaultLanguage !== "javascript" && !isModulesCollapsed && (
          <div
            onMouseDown={handleModulesResizeMouseDown}
            className={`h-1 hover:h-1.5 active:h-1.5 cursor-row-resize select-none transition-all shrink-0 z-20 flex items-center justify-center group ${
              isDark
                ? "bg-[#30363d] hover:bg-purple-500 active:bg-purple-600"
                : "bg-slate-300 hover:bg-purple-500 active:bg-purple-600"
            }`}
            title="Drag to resize Modules/Libraries panel (Min: 90px, Max: 380px)"
          >
            <div
              className={`h-0.5 w-6 rounded-full transition-colors ${
                isDark ? "bg-slate-600 group-hover:bg-white" : "bg-slate-400 group-hover:bg-white"
              }`}
            />
          </div>
        )}

        {/* ========================================================================= */}
        {/* LOWER SECTION: MODULES / LIBRARIES (Installed PIP Packages) - Python Only */}
        {/* ========================================================================= */}
        {defaultLanguage !== "javascript" && (
          <div
            className={`flex flex-col shrink-0 overflow-hidden relative ${
              isModulesCollapsed ? "border-t border-[#30363d]" : ""
            }`}
            style={{
              height: isModulesCollapsed ? "36px" : `${modulesHeight}px`,
              minHeight: isModulesCollapsed ? "36px" : "90px",
              maxHeight: isModulesCollapsed ? "36px" : "380px",
            }}
          >
            {/* Section Header */}
            <div
              onClick={() => setIsModulesCollapsed((prev) => !prev)}
              className={`h-9 px-3 flex items-center justify-between border-b shrink-0 cursor-pointer select-none ${
                isDark
                  ? "bg-[#1f242c] border-[#30363d] text-slate-300 hover:bg-[#252b34]"
                  : "bg-slate-100/90 border-slate-200 text-slate-700 hover:bg-slate-200/90"
              }`}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-xs font-bold truncate">Modules/Libraries</span>
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsModulesCollapsed((prev) => !prev);
                }}
                title={isModulesCollapsed ? "Expand Modules/Libraries panel" : "Collapse Modules/Libraries panel"}
                className={`p-1 rounded hover:bg-slate-700/40 transition cursor-pointer ${
                  isDark ? "text-slate-400 hover:text-white" : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${
                    isModulesCollapsed ? "rotate-180" : ""
                  }`}
                />
              </button>
            </div>

            {/* Section Content: Visible only when expanded */}
            {!isModulesCollapsed && (
              <>
                {/* Installed Packages List or Clean Auto-Install Empty State */}
                <div className="flex-1 overflow-y-auto py-1 space-y-0.5">
                  {installedPackages.length > 0 ? (
                    installedPackages.map((pkg) => (
                      <div
                        key={pkg}
                        className={`px-2.5 py-1.5 flex items-center justify-between text-xs transition-colors ${
                          isDark
                            ? "text-slate-300 hover:bg-[#22272e] hover:text-white"
                            : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate min-w-0">
                          <FaPython className="w-3.5 h-3.5 shrink-0 text-[#38bdf8]" />
                          <span className="truncate text-[11px] font-mono font-bold text-purple-300">{pkg}</span>
                        </div>
                        <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          installed
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-3 text-center flex flex-col items-center justify-center h-full text-slate-400">
                      <Package className="w-5 h-5 mb-1.5 text-slate-500 opacity-60" />
                      <p className="text-[11px] font-semibold text-slate-300 leading-tight">
                        No packages installed yet
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1 leading-snug max-w-[170px]">
                        Packages auto-install when you run code with <code className="text-purple-300 font-mono font-bold">import &lt;pkg&gt;</code> (e.g. <code className="text-amber-300 font-mono">import numpy</code>).
                      </p>
                    </div>
                  )}
                </div>

                {/* PictoBlox-style Floating Circular Purple Action Button (Image 3) */}
                <div className="absolute bottom-2.5 left-2.5 z-20 group">
                  <button
                    type="button"
                    onClick={() => setShowPackageModal(true)}
                    title="Install PIP package"
                    className="w-8 h-8 rounded-full bg-purple-700 hover:bg-purple-600 active:scale-95 text-white shadow-lg shadow-purple-700/40 flex items-center justify-center transition-all cursor-pointer border border-purple-400/30"
                  >
                    <Package className="w-4 h-4" />
                  </button>

                  {/* PictoBlox-inspired Tooltip Popover (Image 3) */}
                  <div className="absolute left-10 bottom-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-all duration-150 z-30 whitespace-nowrap">
                    <div className="bg-[#e11d48] text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-xl flex items-center gap-1.5">
                      <FaPython className="w-3 h-3 text-white" />
                      <span>Install PIP package</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* INTEGRATED RESIZER DIVIDER                                                */}
      {/* ========================================================================= */}
      {onResizeMouseDown && (
        <div
          onMouseDown={onResizeMouseDown}
          className={`w-1.5 hover:w-2 active:w-2 cursor-col-resize select-none transition-all shrink-0 z-20 flex items-center justify-center group ${
            isDark
              ? "bg-[#30363d] hover:bg-purple-500 active:bg-purple-600"
              : "bg-slate-300 hover:bg-purple-500 active:bg-purple-600"
          }`}
          title="Drag to resize Project File Manager (Min: 130px, Max: 380px)"
        >
          <div
            className={`w-0.5 h-6 rounded-full transition-colors ${
              isDark ? "bg-slate-600 group-hover:bg-white" : "bg-slate-400 group-hover:bg-white"
            }`}
          />
        </div>
      )}

      {/* ========================================================================= */}
      {/* PICTOBLOX-STYLE PIP PACKAGE MANAGER MODAL (Image 4)                      */}
      {/* ========================================================================= */}
      {showPackageModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div
            className={`w-full max-w-lg rounded-2xl shadow-2xl flex flex-col overflow-hidden code-workspace-dark-modal ${
              isDark ? "bg-[#181818] text-white shadow-purple-950/20" : "bg-white text-slate-900 shadow-slate-400/20"
            }`}
            style={{
              border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid #e2e8f0",
              outline: "none",
            }}
          >
            {/* Modal Header Bar styled consistent with app UI */}
            <div className="bg-gradient-to-r from-purple-700 to-indigo-700 px-5 py-3.5 flex items-center justify-between text-white shadow-sm">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-white" />
                <h3 className="font-extrabold text-base tracking-wide">Information</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPackageModal(false)}
                className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition cursor-pointer"
                style={{ border: "none", outline: "none" }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Modal Body with PictoBlox Explanation (Image 4) */}
            <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
              <div
                className={`p-4 rounded-xl text-xs leading-relaxed code-workspace-dark-card ${
                  isDark
                    ? "bg-[#161b22] text-slate-300"
                    : "bg-purple-50/50 text-slate-700"
                }`}
                style={{
                  border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(147, 51, 234, 0.2)",
                  outline: "none",
                }}
              >
                <p>
                  In Practido Python in a web browser, you don't need to explicitly install libraries. When you run your code, Practido will automatically install any required libraries based on the <span className="font-mono font-bold text-amber-300">"import module"</span> statements present in your code.
                </p>
                <p className="mt-2 text-slate-400">
                  For instance, if you include the statement <span className="font-mono font-bold text-purple-400">"import numpy"</span>, Practido will handle the installation of the <span className="font-mono font-bold text-purple-400">"numpy"</span> library directly upon clicking <strong>Run Code</strong>.
                </p>
              </div>

              {/* Manual Package Search & Pre-Installation Bar */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1.5">
                  Pre-Install or Search PIP Package
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={manualPkgInput}
                      onChange={(e) => setManualPkgInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleManualInstall();
                      }}
                      placeholder="e.g., numpy, pandas, sympy, scipy..."
                      className={`w-full text-xs rounded-xl pl-8 pr-3 py-2 outline-none focus:ring-1 focus:ring-purple-500 ${
                        isDark
                          ? "bg-[#0d1117] text-white"
                          : "bg-slate-100 text-slate-900"
                      }`}
                      style={{
                        border: isDark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #cbd5e1",
                        outline: "none",
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleManualInstall()}
                    disabled={isInstallingPackage || !manualPkgInput.trim()}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white rounded-xl text-xs font-extrabold flex items-center gap-1.5 transition cursor-pointer"
                    style={{ border: "none", outline: "none" }}
                  >
                    {isInstallingPackage ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Installing...</span>
                      </>
                    ) : (
                      <span>Install</span>
                    )}
                  </button>
                </div>
                {installFeedback && (
                  <p className="text-xs text-purple-400 font-semibold mt-1">{installFeedback}</p>
                )}
              </div>

              {/* Quick 1-Click Popular Packages */}
              <div>
                <span className="text-[11px] font-bold text-slate-400 block mb-2">
                  Popular Student Packages:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {POPULAR_PYTHON_PACKAGES.map((pkg) => {
                    const isAlreadyInstalled = installedPackages.includes(pkg.name);
                    return (
                      <button
                        key={pkg.name}
                        type="button"
                        onClick={() => handleManualInstall(pkg.name)}
                        disabled={isAlreadyInstalled || isInstallingPackage}
                        title={pkg.desc}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                          isAlreadyInstalled
                            ? "bg-emerald-500/20 text-emerald-300 cursor-default"
                            : isDark
                            ? "bg-[#252526] hover:bg-purple-600/30 text-slate-300"
                            : "bg-slate-100 hover:bg-purple-100 text-slate-700"
                        }`}
                        style={{
                          border: isAlreadyInstalled
                            ? "1px solid rgba(16, 185, 129, 0.3)"
                            : isDark
                            ? "1px solid rgba(255, 255, 255, 0.08)"
                            : "1px solid #e2e8f0",
                          outline: "none",
                        }}
                      >
                        {isAlreadyInstalled && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                        <span className="font-mono">{pkg.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Currently Installed Packages in this Session */}
              {installedPackages.length > 0 && (
                <div>
                  <span className="text-[11px] font-bold text-slate-400 block mb-1.5">
                    Installed in this Session ({installedPackages.length}):
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {installedPackages.map((pkg) => (
                      <span
                        key={pkg}
                        className="px-2 py-0.5 rounded bg-purple-900/40 text-purple-300 text-xs font-mono font-semibold"
                        style={{
                          border: "1px solid rgba(168, 85, 247, 0.3)",
                        }}
                      >
                        {pkg}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer with PictoBlox-style Purple 'Okay' Button (Image 4) */}
            <div
              className={`px-5 py-3 flex justify-center ${
                isDark ? "bg-[#1f242c]" : "bg-slate-50"
              }`}
              style={{
                borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid #e2e8f0",
              }}
            >
              <button
                type="button"
                onClick={() => setShowPackageModal(false)}
                className="px-8 py-2 rounded-xl bg-purple-700 hover:bg-purple-600 active:scale-95 text-white font-extrabold text-sm transition cursor-pointer shadow-md shadow-purple-700/20"
                style={{ border: "none", outline: "none" }}
              >
                Okay
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFileManager;
